import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { WorkspaceDomain } from 'honeygrid-types'
import { Globe, Plus } from 'lucide-react'

interface DomainsCardsProps {
	domains: WorkspaceDomain[]
	onSelect: (domain: WorkspaceDomain) => void
	onAdd: () => void
	selectedId?: string
}

const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' => {
	switch (status) {
		case 'verified':
			return 'default'
		case 'pending':
			return 'secondary'
		case 'failed':
		case 'expired':
			return 'destructive'
		default:
			return 'secondary'
	}
}

export function DomainsCards({ domains, onSelect, onAdd, selectedId }: DomainsCardsProps) {
	if (domains.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center text-center space-y-3 bg-[#3A2E2F]/5 h-40 py-8">
				<div className="h-8 w-8 shrink-0 flex items-center justify-center">
					<Globe className="h-8 w-8 text-foreground/60" />
				</div>
				<p className="text-sm font-medium text-muted-foreground">No domains yet</p>
				<Button variant="outline" size="sm" onClick={onAdd}>
					<Plus className="h-4 w-4 mr-2" />
					Add Domain
				</Button>
			</div>
		)
	}

	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
			{domains.map((domain) => (
				<div
					key={domain.uuid}
					className={`border border-[#3A2E2F]/20 rounded-md p-4 cursor-pointer hover:shadow-md transition-shadow ${
						domain.uuid === selectedId ? 'ring-2 ring-primary' : ''
					}`}
					onClick={() => onSelect(domain)}
				>
					<div className="flex items-start gap-3">
						<Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
						<div className="flex-1 min-w-0">
							<div className="font-medium truncate">
								{domain.type === 'subdomain'
									? `${domain.pattern}.tryhoneygrid.com`
									: domain.pattern}
								{domain.is_primary && ' ⭐'}
							</div>
							<div className="text-sm text-muted-foreground truncate capitalize">
								{domain.type}
							</div>
							<div className="flex items-center gap-2 mt-2">
								<Badge variant={getStatusVariant(domain.verification_status)} className="text-xs">
									{domain.verification_status}
								</Badge>
							</div>
						</div>
					</div>
				</div>
			))}
		</div>
	)
}
